import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useQuery} from "@tanstack/react-query";
import {useSearchParams} from "react-router";
import {format, isValid, parse} from "date-fns";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import PrintTemplate from "../../../components/reports/print-reports/print-template.jsx";
import corporationService from "../../../services/web/corporation-service.js";
import garrisonTimelineService from "../../../services/dispatch/garrison-timeline-service.js";
import GarrisonTimelinePrintContent from "./components/timeline/garrison-timeline-print-content.jsx";
import {garrisonTimelineKeys} from "./utils/garrison-timeline-utils.js";
import "../../../css/style-report.css";
import {Card} from "../../../components/card/card.jsx";
import ReportUtils from "../../../utils/report-utils.js";
import useGarrisonTimelineQueries from "./hooks/use-garrison-timeline-queries.js";
import projectUtils from "../../../utils/project-utils.js";
import translateErrorMessage from "../../../utils/translate-error-message.js";
import {garrisonTimelineErrorMappings} from "./utils/garrison-timeline-utils.js";

const filterLabels = {
  dtStart: "Data/hora inicial",
  dtEnd: "Data/hora final",
  leaderId: "Responsável",
  team: "Equipe",
  contingent: "Contingente"
};

const parseNumber = (value, fallback) => {
  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : fallback;
};

const GarrisonTimelinePrint = () => {
  const [searchParams] = useSearchParams();
  const printComponentRef = useRef(null);
  const [headerSelected, setHeaderSelected] = useState(-1);
  const [isPrintMapReady, setIsPrintMapReady] = useState(false);
  const {leaders, teams, contingent, isLoading: isLoadingFilterOptions} = useGarrisonTimelineQueries();
  const filters = useMemo(() => ({
    dtStart: searchParams.get("dtStart"),
    dtEnd: searchParams.get("dtEnd"),
    leaderId: searchParams.get("leaderId") ? Number(searchParams.get("leaderId")) : null,
    team: searchParams.get("team"),
    contingent: searchParams.get("contingent") ? Number(searchParams.get("contingent")) : null
  }), [searchParams]);
  const zoom = useMemo(() => ({
    start: parseNumber(searchParams.get("zoomStart"), 0),
    end: parseNumber(searchParams.get("zoomEnd"), 100)
  }), [searchParams]);
  const elapsedSeconds = parseNumber(searchParams.get("elapsedSeconds"), 0);
  const baseStart = filters.dtStart ? parse(filters.dtStart, "dd/MM/yyyy HH:mm", new Date()) : null;
  const instant = baseStart && isValid(baseStart)
    ? format(new Date(baseStart.getTime() + elapsedSeconds * 1000), "dd/MM/yyyy HH:mm")
    : null;
  const isValidRequest = Boolean(filters.dtStart && filters.dtEnd && instant);
  const {data: printData, isLoading: isLoadingPrint, isError: isPrintError, error: printError} = useQuery({
    queryKey: garrisonTimelineKeys.print(filters),
    queryFn: () => garrisonTimelineService.print(filters),
    enabled: isValidRequest,
    retry: 0
  });
  const {data: snapshot = [], isLoading: isLoadingSnapshot, isError: isSnapshotError} = useQuery({
    queryKey: garrisonTimelineKeys.snapshot(filters, instant),
    queryFn: () => garrisonTimelineService.snapshot({...filters, instant}),
    enabled: isValidRequest,
    retry: 0
  });
  const {data: corporation = null, isLoading: isLoadingCorporation} = useQuery({
    queryKey: garrisonTimelineKeys.corporation(),
    queryFn: corporationService.entity
  });
  const appliedFilters = useMemo(() => ({
    ...filters,
    leaderId: leaders?.find(option => String(option.value) === String(filters.leaderId)) ?? filters.leaderId,
    team: teams?.find(option => String(option.value) === String(filters.team)) ?? filters.team,
    contingent: contingent?.find(option => String(option.value) === String(filters.contingent)) ?? filters.contingent
  }), [contingent, filters, leaders, teams]);

  useEffect(() => {
    if (!isPrintError) return;

    projectUtils.swalErrorMessage(
      "",
      translateErrorMessage(
        printError,
        "Não foi possível preparar a impressão da linha do tempo.",
        garrisonTimelineErrorMappings
      ),
      "Ok"
    );
  }, [isPrintError, printError]);

  useEffect(() => {
    if (!printData) return;

    ReportUtils.appendFilters(ReportUtils.filters(appliedFilters, filterLabels));
  }, [appliedFilters, printData]);
  const handlePrintMapReady = useCallback(() => setIsPrintMapReady(true), []);

  return (
    <Template isLoading={isLoadingFilterOptions || isLoadingPrint || isLoadingSnapshot || isLoadingCorporation}>
      <Template.Header>
        <div className="flex w-full justify-between">
          <Template.Title>Linha do tempo da guarnição</Template.Title>
          <HeaderButtons
            printable={true}
            printComponentRef={printComponentRef}
            reportData={printData?.garrisons}
            data={printData?.garrisons?.length}
            headerChange={true}
            headerSelected={headerSelected}
            setHeaderSelected={setHeaderSelected}
            canPrint={isPrintMapReady}
          />
        </div>
      </Template.Header>
      <Template.Body>
        <Card>
          <Card.Body className="px-6">
            {printData?.garrisons?.length > 0 && (
              <PrintTemplate
                ref={printComponentRef}
                title="Linha do tempo da guarnição"
                entity={headerSelected}
                landscape={true}
                printClassName="text-[#676a6c] print:text-black"
              >
                <GarrisonTimelinePrintContent
                  data={printData}
                  filter={filters}
                  zoom={zoom}
                  corporation={corporation}
                  snapshot={snapshot}
                  elapsedSeconds={elapsedSeconds}
                  onPrintMapReady={handlePrintMapReady}
                />
              </PrintTemplate>
            )}
          </Card.Body>
        </Card>
      </Template.Body>
    </Template>
  );
};

export default GarrisonTimelinePrint;
